import { AppError } from "@/lib/utils/errors";
import {
  randomBytes,
  pbkdf2Sync,
  createCipheriv,
  createDecipheriv,
} from "crypto";

export class Encryption {
  key = process.env.SECRET_KEY || "your-secret-key";
  algorithm = "aes-256-cbc";
  ivLength = 16;
  saltLength = 64;

  // Add a private cache property
  private decryptCache: Map<string, { value: string; timestamp: number }> =
    new Map();
  private readonly cacheExpirationTime = 7 * 24 * 60 * 60 * 1000; // 1 week in milliseconds
  private readonly cleanupInterval = 1000 * 60 * 15; // 15 minutes
  private cleanupTimer?: ReturnType<typeof setInterval>;

  constructor() {
    // Start the automatic cleanup process
    this.startAutoCleanup();
  }

  private startAutoCleanup(): void {
    // Clear any existing timer
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    // Set up periodic cleanup
    this.cleanupTimer = setInterval(() => {
      this.cleanExpiredCache();
      this.logCacheStatus("Auto cleanup");
    }, this.cleanupInterval);

    // Ensure the timer doesn't prevent the process from exiting
    try {
      if (this.cleanupTimer && typeof this.cleanupTimer.unref === "function") {
        this.cleanupTimer.unref();
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.logError();
    }
  }

  private logCacheStatus(context: string): void {
    if (typeof window !== "undefined") {
      console.log(`[Cache Status - ${context}] ${new Date().toISOString()}`);
      console.log(`- Cache size: ${this.decryptCache.size} entries`);
      console.log(`- Cache contents:`);

      this.decryptCache.forEach((value, key) => {
        const ageInMinutes = Math.round(
          (Date.now() - value.timestamp) / (1000 * 60),
        );
        const truncatedKey = `${key.substring(0, 20)}...`;
        const truncatedValue = `${value.value.substring(0, 20)}...`;
        console.log(`  • Key: ${truncatedKey}`);
        console.log(`    Value: ${truncatedValue}`);
        console.log(`    Age: ${ageInMinutes} minutes`);
      });

      console.log("----------------------------------------");
    }
  }

  encrypt(text: string): string {
    const iv = randomBytes(this.ivLength);
    const salt = randomBytes(this.saltLength);

    const key = pbkdf2Sync(this.key, salt, 100000, 32, "sha512");
    const cipher = createCipheriv(this.algorithm, key, iv);

    const encrypted = Buffer.concat([
      cipher.update(text, "utf8"),
      cipher.final(),
    ]);

    return Buffer.concat([salt, iv, encrypted]).toString("base64");
  }

  decrypt(encryptedData: string): string {
    // Check cache first
    const cached = this.decryptCache.get(encryptedData);
    if (cached) {
      // Check if cache is still valid
      if (Date.now() - cached.timestamp < this.cacheExpirationTime) {
        if (typeof window !== "undefined") {
          console.log("Cache hit", new Date().toISOString());
        }
        return cached.value;
      } else {
        // Remove expired cache entry
        this.decryptCache.delete(encryptedData);
      }
    }

    // Perform decryption
    const buffer = Buffer.from(encryptedData, "base64");

    const salt = buffer.subarray(0, this.saltLength);
    const iv = buffer.subarray(
      this.saltLength,
      this.saltLength + this.ivLength,
    );
    const encrypted = buffer.subarray(this.saltLength + this.ivLength);

    const key = pbkdf2Sync(this.key, salt, 100000, 32, "sha512");
    const decipher = createDecipheriv(this.algorithm, key, iv);

    const decrypted = Buffer.concat([
      decipher.update(encrypted),
      decipher.final(),
    ]).toString("utf8");

    // Store in cache
    this.decryptCache.set(encryptedData, {
      value: decrypted,
      timestamp: Date.now(),
    });

    this.logCacheStatus("New Cache Entry");
    return decrypted;
  }

  // Add method to clear cache
  clearCache(): void {
    const previousSize = this.decryptCache.size;
    this.decryptCache.clear();

    if (typeof window !== "undefined") {
      console.log(
        `[Cache Cleared] Removed ${previousSize} entries at ${new Date().toISOString()}`,
      );
    }
  }

  // Add method to remove expired cache entries
  cleanExpiredCache(): void {
    const now = Date.now();
    let removedCount = 0;
    for (const [key, entry] of this.decryptCache.entries()) {
      if (now - entry.timestamp >= this.cacheExpirationTime) {
        this.decryptCache.delete(key);
        removedCount++;
      }
    }

    if (removedCount > 0) {
      if (typeof window !== "undefined") {
        console.log(
          `[Cache Cleanup] Removed ${removedCount} expired entries at ${new Date().toISOString()}`,
        );
      }
    }
  }

  isEncrypted(text: string): boolean {
    try {
      const buffer = Buffer.from(text, "base64");
      const minLength = this.saltLength + this.ivLength + 1;

      if (buffer.length < minLength) {
        return false;
      }

      this.decrypt(text);
      return true;
    } catch (error) {
      const appErr = new AppError(error);
      appErr.logError("src/lib/encryption/encryption.ts isEncrypted");
      return false;
    }
  }

  // Cleanup method to be called when the instance is no longer needed
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
  }
}

export const encryption = new Encryption();
